// Connect-link redirect origins (rbac-restore R3 3.2, deferred until GET /workspaces/{id}
// returned the current list; built in simple-mode S4).
//
//   GET   /workspaces/{id}                            any member → connect_redirect_origins
//   PATCH /workspaces/{id}/connect-redirect-origins   manage_auth_configs  ← {origins: [...]}
//
// The PATCH REPLACES the whole list, so the editor starts from the stored list and
// always sends the full edited list. Each origin must be an exact https origin (no path,
// query, fragment or credentials), at most 20 — the same rules the route enforces.
// Without `manage_auth_configs` the list is shown read-only.
import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { Workspace } from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { MAX_REDIRECT_ORIGINS, parseOrigin } from './redirectOrigins'

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((x, i) => x === b[i])
}

export function RedirectOriginsSection({ workspace }: { workspace: Workspace }) {
  const { workspaceId } = useSession()
  const canEdit = useCan('manage_auth_configs')
  const qc = useQueryClient()
  const stored = workspace.connect_redirect_origins ?? []
  const [draft, setDraft] = useState<string[]>(stored)
  const [input, setInput] = useState('')
  const [inputError, setInputError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (origins: string[]) =>
      apiFetch<{ workspace_id: string; connect_redirect_origins: string[] }>(
        `/workspaces/${workspaceId}/connect-redirect-origins`,
        { method: 'PATCH', body: JSON.stringify({ origins }) },
      ),
    onSuccess: (res, sent) => {
      const next = res?.connect_redirect_origins ?? sent
      setDraft(next)
      qc.setQueryData<Workspace>(qk.workspace(workspaceId), (old) => (old ? { ...old, connect_redirect_origins: next } : old))
    },
  })

  const add = (e: FormEvent) => {
    e.preventDefault()
    const parsed = parseOrigin(input)
    if ('error' in parsed) return setInputError(parsed.error)
    if (draft.includes(parsed.origin)) return setInputError(`${parsed.origin} is already in the list.`)
    if (draft.length >= MAX_REDIRECT_ORIGINS) return setInputError(`At most ${MAX_REDIRECT_ORIGINS} origins.`)
    save.reset()
    setDraft([...draft, parsed.origin])
    setInput('')
    setInputError(null)
  }

  const dirty = !sameList(draft, stored)

  return (
    <section className="card-section" aria-labelledby="ws-origins-title">
      <h2 id="ws-origins-title" className="section-title">Connect-link redirect origins</h2>
      <p className="muted">
        Where a connect link may send an end-user back to after they connect an app. Only these exact https origins are
        accepted.
      </p>
      {draft.length === 0 ? (
        <p className="cell-sub">No origins yet: connect links can’t redirect anywhere.</p>
      ) : (
        <ul className="origin-list" aria-label="Redirect origins">
          {draft.map((o) => (
            <li key={o}>
              <code>{o}</code>
              {canEdit && (
                <button
                  type="button"
                  className="btn btn--sm btn--ghost-danger"
                  aria-label={`Remove ${o}`}
                  onClick={() => {
                    save.reset()
                    setDraft(draft.filter((x) => x !== o))
                  }}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit ? (
        <>
          <form className="inline-form" aria-label="Add redirect origin" onSubmit={add}>
            <div className="field">
              <label htmlFor="ws-origin-new">Origin</label>
              <input
                id="ws-origin-new"
                value={input}
                placeholder="https://app.example.com"
                maxLength={2048}
                aria-invalid={!!inputError}
                aria-describedby={inputError ? 'ws-origin-error' : undefined}
                onChange={(e) => {
                  setInput(e.target.value)
                  setInputError(null)
                }}
              />
            </div>
            <button type="submit" className="btn" disabled={!input.trim()}>
              Add
            </button>
          </form>
          {inputError && (
            <p className="form-error" id="ws-origin-error" role="alert">
              {inputError}
            </p>
          )}
          <div className="row-gap">
            <button
              type="button"
              className="btn btn--primary"
              disabled={!dirty || save.isPending}
              aria-busy={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              {save.isPending ? 'Saving…' : 'Save origins'}
            </button>
            {dirty && !save.isPending && (
              <button type="button" className="btn" onClick={() => { save.reset(); setDraft(stored) }}>
                Discard changes
              </button>
            )}
          </div>
          {save.isSuccess && !dirty && <p className="muted" role="status">Saved.</p>}
          {save.error ? <p className="form-error" role="alert">{errorMessage(save.error)}</p> : null}
        </>
      ) : (
        <p className="muted" role="note">Only members who can manage auth configs can change this list.</p>
      )}
    </section>
  )
}
